import { useState } from 'react';
import { useNavigate } from 'react-router';
import { t } from 'i18next';
import { Alert, Button, Surface, Text } from '@datanomik/core-ui';
import { AlertLine, ArrowDownSLine, CheckLine, ExchangeLine } from '@datanomik/core-ui/icons';

export interface ErrorStateProps {
  error?: Error | null;
  onRetry?: () => void;
  reference?: string;
}

const isDev = import.meta.env.DEV;

export function ErrorState({ error, onRetry, reference }: ErrorStateProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const detail = [
    reference && `ref: ${reference}`,
    error?.message && `error: ${error.message}`,
    `url: ${window.location.href}`,
    `when: ${new Date().toISOString()}`,
  ].filter(Boolean).join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(detail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  };

  return (
    <div
      data-modern
      role="alert"
      className="flex min-h-[60vh] items-center justify-center p-6 font-sans"
    >
      <Surface depth={2} padding="lg" className="flex w-full max-w-md flex-col gap-5">
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-destructive-subtle text-destructive-subtle-foreground [&_svg]:size-5">
            <AlertLine aria-hidden="true" />
          </span>
          <div className="flex flex-col gap-1">
            <Text variant="heading" as="h1">{t('Something went wrong!')}</Text>
            <Text variant="body-sm" tone="muted">
              {t('This section could not be loaded. Trying again usually solves it.')}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {onRetry && (
            <Button className="w-full" onClick={onRetry}>
              <ExchangeLine aria-hidden="true" />
              {t('Try again')}
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
            {t('Reload the page')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/')}>
            {t('Back to home page')}
          </Button>
        </div>

        {detail && (
          <div className="flex flex-col gap-2 border-t border-border pt-4">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="link"
                className="gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => setShowDetail((open) => !open)}
                aria-expanded={showDetail}
              >
                <ArrowDownSLine
                  aria-hidden="true"
                  className={showDetail ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
                {t('Technical details')}
              </Button>
              <Button variant="ghost" onClick={copy}>
                {copied ? <CheckLine aria-hidden="true" /> : null}
                {copied ? t('Copied') : t('Copy')}
              </Button>
            </div>

            {showDetail && (
              <pre className="max-h-40 overflow-auto rounded-lg bg-surface-0 p-3 font-mono text-xs text-muted-foreground">
                {detail}
              </pre>
            )}

            {isDev && error?.stack && showDetail && (
              <Alert tone="warning">{t('Stack shown in development only.')}</Alert>
            )}
          </div>
        )}
      </Surface>
    </div>
  );
}
